'use client';

/**
 * QuickContactModal — V3 migration.
 *
 * Replaces the V2 Flowbite light modal with a hand-rolled V3 dark
 * dialog. The Flowbite Modal couldn't be themed cleanly to match the
 * surrounding V3 page; a plain fixed overlay with V3 surface tokens is
 * simpler and avoids a jarring white panel popping over a dark page.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { HiCheck, HiX } from 'react-icons/hi';
import { Locale } from '../../../../i18n-config';
import { useTranslation } from '@/i18n/client';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';

type Props = {
  lang: Locale;
  show: boolean;
  onClose: () => void;
};

const QuickContactModalContent = ({ lang, show, onClose }: Props) => {
  const { t } = useTranslation(lang);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [formData, setFormData] = useState({ email: '', message: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClose = useCallback(() => {
    if (isLoading) return;
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    setFormData({ email: '', message: '' });
    setError('');
    setIsSuccess(false);
    onClose();
  }, [isLoading, onClose]);

  // Lock body scroll while open + close on Escape
  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [show, handleClose]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const handleReCaptchaVerify = useCallback(async () => {
    try {
      if (!executeRecaptcha) {
        console.warn('reCAPTCHA not ready');
        return null;
      }
      return await executeRecaptcha('contact_form');
    } catch (e) {
      console.error('reCAPTCHA error:', e);
      return null;
    }
  }, [executeRecaptcha]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.email.trim() || !formData.message.trim()) {
      setError(t('ContactUs/Quick/required_fields'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('ContactUs/Section1/key11'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const token = await handleReCaptchaVerify();
      if (!token) {
        setError(t('ContactUs/Quick/recaptcha_error'));
        return;
      }

      const submissionData = {
        name: `Quick Contact (${formData.email})`,
        email: formData.email,
        country: 'Not specified',
        requestSubject: 'Quick Contact',
        message: `=== Quick Contact Form ===
Email: ${formData.email}
Contact Method: Quick Modal
============================

Message:
${formData.message}`,
        recaptcha_token: token,
      };

      const response = await fetch('/sum/api/contact-us', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionData),
      });

      if (response.ok) {
        setIsSuccess(true);
        setFormData({ email: '', message: '' });
        successTimerRef.current = setTimeout(() => {
          setIsSuccess(false);
          successTimerRef.current = null;
          onClose();
        }, 3000);
      } else {
        setError(t('ContactUs/Quick/error_message'));
      }
    } catch {
      setError(t('ContactUs/Quick/error_message'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) return null;

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 500,
    color: '#B4C1D8',
    marginBottom: 6,
    letterSpacing: '-0.01em',
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10,
    color: '#F5F7FA',
    fontSize: 15,
    fontFamily: 'inherit',
    outline: 'none',
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('ContactUs/Quick/title')}
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(10,14,39,0.78)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 480,
          background: '#0E1130',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 18,
          boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
          overflow: 'hidden',
        }}
      >
        {isSuccess ? (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 999,
                background: 'rgba(34,211,238,0.1)',
                border: '1px solid rgba(34,211,238,0.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <HiCheck style={{ width: 32, height: 32, color: '#22D3EE' }} />
            </div>
            <h3
              style={{
                fontSize: 22,
                fontWeight: 700,
                color: '#F5F7FA',
                letterSpacing: '-0.01em',
                margin: '0 0 8px',
              }}
            >
              {t('ContactUs/Quick/success_title')}
            </h3>
            <p style={{ color: '#B4C1D8', fontSize: 15, lineHeight: 1.5, margin: '0 0 20px' }}>
              {t('ContactUs/Quick/success_message')}
            </p>
            <button onClick={handleClose} className="sum-cta-primary">
              {t('ContactUs/Quick/close')}
            </button>
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '18px 22px',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <h3
                style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#F5F7FA',
                  letterSpacing: '-0.01em',
                  margin: 0,
                }}
              >
                {t('ContactUs/Quick/title')}
              </h3>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6B7593',
                  cursor: 'pointer',
                  padding: 4,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 6,
                  transition: 'color 0.15s, background 0.15s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#F5F7FA';
                  e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#6B7593';
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <HiX style={{ width: 20, height: 20 }} />
              </button>
            </div>

            <div style={{ padding: '20px 22px 22px' }}>
              <p style={{ color: '#B4C1D8', fontSize: 14, lineHeight: 1.5, margin: '0 0 16px' }}>
                {t('ContactUs/Quick/subtitle')}
              </p>

              {error && (
                <div
                  role="alert"
                  style={{
                    background: 'rgba(255,77,77,0.08)',
                    border: '1px solid rgba(255,77,77,0.35)',
                    borderRadius: 10,
                    color: '#FF8A8A',
                    fontSize: 13,
                    lineHeight: 1.5,
                    padding: '10px 12px',
                    marginBottom: 14,
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label htmlFor="quick-email" style={labelStyle}>
                    {t('ContactUs/Quick/email_label')}
                  </label>
                  <input
                    id="quick-email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder={t('ContactUs/Quick/email_placeholder')}
                    required
                    style={fieldStyle}
                  />
                </div>

                <div>
                  <label htmlFor="quick-message" style={labelStyle}>
                    {t('ContactUs/Quick/message_label')}
                  </label>
                  <textarea
                    id="quick-message"
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                    placeholder={t('ContactUs/Quick/message_placeholder')}
                    required
                    style={{ ...fieldStyle, resize: 'vertical', minHeight: 100 }}
                  />
                </div>

                <div style={{ display: 'flex', gap: 10, paddingTop: 6 }}>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="sum-cta-primary"
                    style={{ flex: 1, opacity: isLoading ? 0.6 : 1 }}
                  >
                    {isLoading ? '…' : t('ContactUs/Quick/send')}
                  </button>
                  <button type="button" onClick={handleClose} disabled={isLoading} className="sum-cta-ghost">
                    {t('ContactUs/Quick/cancel')}
                  </button>
                </div>
              </form>

              <p
                className="mono"
                style={{
                  fontSize: 11,
                  color: '#6B7593',
                  textAlign: 'center',
                  marginTop: 14,
                  letterSpacing: 1,
                }}
              >
                {t('ContactUs/Quick/privacy_note')}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const QuickContactModal = ({ lang, show, onClose }: Props) => {
  if (!show) return null;
  return (
    <GoogleReCaptchaProvider useEnterprise={true} reCaptchaKey={process.env.NEXT_PUBLIC_SITEKEY_V3 as string}>
      <QuickContactModalContent lang={lang} show={show} onClose={onClose} />
    </GoogleReCaptchaProvider>
  );
};

export default QuickContactModal;
